import React, { useMemo, useState } from "react";
import { Copy, Trash2 } from "lucide-react";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { ApiEndpointReference } from "./ApiEndpointReference";
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
} from "@/hooks/useApiKeys";
import { useStartupContext } from "@/hooks/useStartupContext";
import type {
  ApiKeyExpirationOption,
  ApiKeySummary,
} from "@/types/api-key.types";

const EXPIRATION_OPTIONS: Array<{
  value: ApiKeyExpirationOption;
  label: string;
}> = [
  { value: "never", label: "Never expires" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "1y", label: "1 year" },
  { value: "custom", label: "Custom date" },
];

function formatDateTime(value: string | null): string {
  if (!value) {
    return "Never";
  }

  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function resolveExpirationDate(
  option: ApiKeyExpirationOption,
  customDate: string,
): string | null {
  if (option === "never") {
    return null;
  }

  if (option === "custom") {
    if (!customDate) {
      throw new Error("Choose a custom expiration date.");
    }

    const parsed = new Date(`${customDate}T23:59:59`);
    if (Number.isNaN(parsed.getTime())) {
      throw new Error("Invalid expiration date.");
    }

    if (parsed.getTime() <= Date.now()) {
      throw new Error("Expiration date must be in the future.");
    }

    return parsed.toISOString();
  }

  const expiresAt = new Date();
  if (option === "30d") {
    expiresAt.setDate(expiresAt.getDate() + 30);
  } else if (option === "90d") {
    expiresAt.setDate(expiresAt.getDate() + 90);
  } else if (option === "1y") {
    expiresAt.setFullYear(expiresAt.getFullYear() + 1);
  }

  return expiresAt.toISOString();
}

function statusClasses(status: ApiKeySummary["status"]): string {
  if (status === "active") {
    return "border-brand/30 bg-brand/10 text-brand";
  }

  if (status === "expired") {
    return "border-warn/30 bg-warn/10 text-warn";
  }

  return "border-white/15 bg-surface/10 text-white/55";
}

function scopeLabel(apiKey: ApiKeySummary): string {
  const startupIds = apiKey.startupIds ?? null;
  const startupScope = apiKey.startupScope ?? (startupIds?.length ? "scoped" : "all");

  if (startupScope === "scoped" && startupIds?.length) {
    return `${startupIds.length} startup${startupIds.length === 1 ? "" : "s"}`;
  }

  return "All startups";
}

function ApiKeyRow({
  apiKey,
  onRevoke,
  isRevoking,
}: {
  apiKey: ApiKeySummary;
  onRevoke: (keyId: string) => void;
  isRevoking: boolean;
}) {
  return (
    <div className="rounded-[14px] border border-white/10 bg-surface-elevated px-3 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-white">{apiKey.name}</span>
            <span
              className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusClasses(apiKey.status)}`}
            >
              {apiKey.status}
            </span>
          </div>
          <p className="mt-1 font-mono text-xs text-white/55">{apiKey.prefix}</p>
          <div className="mt-2 grid gap-1 text-xs text-white/45 sm:grid-cols-2">
            <span>Created {formatDateTime(apiKey.createdAt)}</span>
            <span>Expires {formatDateTime(apiKey.expiresAt)}</span>
            <span>Last used {formatDateTime(apiKey.lastUsedAt)}</span>
            <span>Access {(apiKey.readOnly ?? false) ? "Read-only" : "Read & write"}</span>
            <span>Scope {scopeLabel(apiKey)}</span>
            {apiKey.revokedAt ? (
              <span>Revoked {formatDateTime(apiKey.revokedAt)}</span>
            ) : null}
          </div>
        </div>

        {apiKey.status === "active" ? (
          <button
            type="button"
            onClick={() => onRevoke(apiKey.id)}
            disabled={isRevoking}
            className="inline-flex items-center gap-1.5 rounded-[10px] border border-danger/30 px-2.5 py-1.5 text-xs font-medium text-danger transition-colors hover:bg-danger/10 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Revoke
          </button>
        ) : null}
      </div>
    </div>
  );
}

export interface ApiKeysPanelProps {
  embedded?: boolean;
}

export const ApiKeysPanel: React.FC<ApiKeysPanelProps> = ({ embedded = false }) => {
  const [name, setName] = useState("");
  const [expirationOption, setExpirationOption] =
    useState<ApiKeyExpirationOption>("never");
  const [customExpirationDate, setCustomExpirationDate] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [scopeAllStartups, setScopeAllStartups] = useState(true);
  const [selectedStartupIds, setSelectedStartupIds] = useState<string[]>([]);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { availableStartups } = useStartupContext();
  const { data: apiKeys = [], isLoading, error } = useApiKeys();
  const createMutation = useCreateApiKey();
  const revokeMutation = useRevokeApiKey();

  const apiBaseUrl = useMemo(
    () =>
      import.meta.env.VITE_API_BASE_URL || "https://api-q5sqhbgfya-uc.a.run.app",
    [],
  );

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setFeedback(null);
    setCreatedKey(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setFeedback({ type: "error", message: "Enter a name for this API key." });
      return;
    }

    try {
      const expiresAt = resolveExpirationDate(
        expirationOption,
        customExpirationDate,
      );

      if (!scopeAllStartups && selectedStartupIds.length === 0) {
        setFeedback({
          type: "error",
          message: "Select at least one startup or choose all startups.",
        });
        return;
      }

      const result = await createMutation.mutateAsync({
        name: trimmedName,
        expiresAt,
        readOnly,
        startupIds: scopeAllStartups ? null : selectedStartupIds,
      });

      setName("");
      setExpirationOption("never");
      setCustomExpirationDate("");
      setReadOnly(false);
      setScopeAllStartups(true);
      setSelectedStartupIds([]);
      setCreatedKey(result.key);
      setFeedback({
        type: "success",
        message: "API key created. Copy it now — it won't be shown again.",
      });
    } catch (createError) {
      const message =
        createError instanceof Error
          ? createError.message
          : "Could not create API key.";
      setFeedback({ type: "error", message });
    }
  };

  const handleRevoke = async (keyId: string) => {
    setFeedback(null);

    if (
      !window.confirm(
        "Revoke this API key? Any integrations using it will stop working immediately.",
      )
    ) {
      return;
    }

    try {
      await revokeMutation.mutateAsync(keyId);
      setFeedback({ type: "success", message: "API key revoked." });
    } catch (revokeError) {
      const message =
        revokeError instanceof Error
          ? revokeError.message
          : "Could not revoke API key.";
      setFeedback({ type: "error", message });
    }
  };

  const handleCopy = async () => {
    if (!createdKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(createdKey);
      setFeedback({ type: "success", message: "API key copied to clipboard." });
    } catch {
      setFeedback({
        type: "error",
        message: "Could not copy automatically. Select and copy the key manually.",
      });
    }
  };

  const content = (
    <>
      <p className="text-sm text-white/60">
        Generate keys for programmatic access to the BurnChart API. Use{" "}
        <code className="rounded bg-surface/20 px-1 py-0.5 text-xs">
          Authorization: Bearer &lt;api_key&gt;
        </code>
        .
      </p>
      <p className="mt-2 text-xs text-white/45">
        Base URL: {apiBaseUrl}
      </p>

      <form
        onSubmit={handleCreate}
        className="mt-5 rounded-[16px] border border-white/10 bg-surface/20 p-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Key name">
            <Input
              id="api-key-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Production integration"
            />
          </FormField>

          <FormField label="Expiration">
            <select
              id="api-key-expiration"
              value={expirationOption}
              onChange={(event) =>
                setExpirationOption(event.target.value as ApiKeyExpirationOption)
              }
              className="w-full rounded-[14px] border border-white/10 bg-surface px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand/40"
            >
              {EXPIRATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        {expirationOption === "custom" ? (
          <div className="mt-4">
            <FormField label="Custom expiration date">
              <Input
                id="api-key-custom-expiration"
                type="date"
                value={customExpirationDate}
                onChange={(event) => setCustomExpirationDate(event.target.value)}
              />
            </FormField>
          </div>
        ) : null}

        <div className="mt-4 space-y-4 rounded-[14px] border border-white/10 bg-surface/10 p-4">
          <label className="flex items-start gap-3 text-sm text-white/75">
            <input
              type="checkbox"
              checked={readOnly}
              onChange={(event) => setReadOnly(event.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold text-white">Read-only</span>
              <span className="mt-0.5 block text-xs text-white/45">
                Allow GET requests only. Existing keys without this flag keep full read/write access.
              </span>
            </span>
          </label>

          <div>
            <p className="text-sm font-semibold text-white">Startup scope</p>
            <p className="mt-0.5 text-xs text-white/45">
              Limit this key to specific startups, or leave all startups selected for full account access.
            </p>

            <label className="mt-3 flex items-center gap-2 text-sm text-white/75">
              <input
                type="radio"
                name="api-key-scope"
                checked={scopeAllStartups}
                onChange={() => {
                  setScopeAllStartups(true);
                  setSelectedStartupIds([]);
                }}
              />
              All startups I can access
            </label>

            <label className="mt-2 flex items-center gap-2 text-sm text-white/75">
              <input
                type="radio"
                name="api-key-scope"
                checked={!scopeAllStartups}
                onChange={() => setScopeAllStartups(false)}
              />
              Selected startups only
            </label>

            {!scopeAllStartups ? (
              <div className="mt-3 max-h-40 space-y-2 overflow-y-auto rounded-[12px] border border-white/10 p-3">
                {availableStartups.length === 0 ? (
                  <p className="text-xs text-white/45">
                    No startups available to scope. Create a startup first.
                  </p>
                ) : (
                  availableStartups.map((startup) => {
                    const checked = selectedStartupIds.includes(startup.id);
                    return (
                      <label
                        key={startup.id}
                        className="flex items-center gap-2 text-sm text-white/75"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => {
                            setSelectedStartupIds((current) =>
                              event.target.checked
                                ? [...current, startup.id]
                                : current.filter((id) => id !== startup.id),
                            );
                          }}
                        />
                        <span className="truncate">{startup.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Generate API key"}
          </Button>
        </div>
      </form>

      {createdKey ? (
        <div className="mt-4 rounded-[14px] border border-brand/30 bg-brand/10 p-4">
          <p className="text-sm font-medium text-brand">
            Copy your new API key now
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="block flex-1 overflow-x-auto rounded-[10px] bg-surface px-3 py-2 font-mono text-xs text-white">
              {createdKey}
            </code>
            <Button type="button" variant="secondary" size="sm" onClick={handleCopy}>
              <Copy className="mr-1.5 h-4 w-4" />
              Copy
            </Button>
          </div>
        </div>
      ) : null}

      {feedback ? (
        <p
          className={`mt-4 text-sm ${
            feedback.type === "success" ? "text-brand" : "text-danger"
          }`}
        >
          {feedback.message}
        </p>
      ) : null}

      <div className="mt-6 space-y-3">
        <h4 className="text-sm font-semibold text-white/75">Your API keys</h4>

        {isLoading ? (
          <p className="text-sm text-white/45">Loading API keys...</p>
        ) : null}

        {error ? (
          <p className="text-sm text-danger">Could not load API keys.</p>
        ) : null}

        {!isLoading && !error && apiKeys.length === 0 ? (
          <p className="rounded-[14px] border border-dashed border-white/10 px-4 py-6 text-sm text-white/45">
            No API keys yet. Create one to connect external tools and automations.
          </p>
        ) : null}

        {apiKeys.map((apiKey) => (
          <ApiKeyRow
            key={apiKey.id}
            apiKey={apiKey}
            onRevoke={handleRevoke}
            isRevoking={revokeMutation.isPending}
          />
        ))}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-6">
        <SettingsSectionCard
          title="API access"
          description="Create and revoke keys for scripts, integrations, and external tools."
        >
          {content}
        </SettingsSectionCard>
        <ApiEndpointReference apiBaseUrl={apiBaseUrl} />
      </div>
    );
  }

  return <section className="mt-8 border-t border-white/10 pt-6">{content}</section>;
};
